import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import {
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import {
  editAutomationRule,
  saveAutomationRule,
  getAutomationRules,
} from "api/apiService";
import { notifyCenter } from "utils/toast";

// Reuse the SELECT_SX from the list view to keep behaviour consistent.
const SELECT_SX = {
  height: 40,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

const ACTION_LABELS = {
  send_email: "Send e-post",
  send_vendor_push: "Vendor-push",
  push_to_wc: "Push til WC",
};

const PUSH_TO_WC_KINDS = [
  { value: "status", label: "Status" },
  { value: "delivery_date", label: "Leveringsdato" },
  { value: "order", label: "Hele ordren" },
];

// Localised event-type labels — keep the technical key visible so admin
// recognises it from API logs / docs.
const EVENT_LABELS = {
  order_created: "Ordre opprettet",
  status_changed: "Status endret",
  wc_status_changed: "WC-status endret",
  payment_received: "Betaling mottatt",
  order_refunded: "Refundert",
  order_note_added: "Notat lagt til",
  delivery_date_changed: "Leveringsdato endret",
  date_change_denied: "Dato-endring avvist",
};

function emptyForm() {
  return {
    name: "",
    enabled: true,
    triggerType: "event",
    eventType: "",
    // Schedule fields — only relevant when triggerType='schedule'.
    scheduleAnchor: "delivery.date",
    scheduleAmount: 24,             // human-friendly number
    scheduleUnit: "hours",          // 'minutes' | 'hours' | 'days'
    scheduleDirection: "before",    // 'before' | 'after'
    actionType: "send_email",
    actionConfig: { emailType: "" },
    delayMinutes: 0,
    notes: "",
  };
}

// Convert UI fields (amount + unit + direction) → signed minutes for storage.
function offsetMinFromForm(amount, unit, direction) {
  const a = Math.max(0, Number(amount) || 0);
  const perUnit = unit === "minutes" ? 1 : unit === "hours" ? 60 : 1440;
  const sign = direction === "before" ? -1 : 1;
  return sign * a * perUnit;
}

// Reverse: given a signed minutes value from DB, derive amount + unit + direction.
// Picks the largest unit that divides cleanly (e.g. -1440 → 1 day before).
function formFromOffsetMin(offsetMin) {
  const total = Number(offsetMin) || 0;
  const direction = total < 0 ? "before" : "after";
  const abs = Math.abs(total);
  if (abs === 0) return { scheduleAmount: 0, scheduleUnit: "minutes", scheduleDirection: "after" };
  if (abs % 1440 === 0) return { scheduleAmount: abs / 1440, scheduleUnit: "days", scheduleDirection: direction };
  if (abs % 60 === 0)   return { scheduleAmount: abs / 60,   scheduleUnit: "hours", scheduleDirection: direction };
  return { scheduleAmount: abs, scheduleUnit: "minutes", scheduleDirection: direction };
}

function CreateAutomationRule() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm());
  const [meta, setMeta] = useState({
    event_types: [],
    action_types: ["send_email", "send_vendor_push", "push_to_wc"],
    email_types: [],
    email_type_options: [], // [{ value, label, templates: [...] }]
  });
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Initial load: either fetch existing rule (edit) or just fetch metadata
  // (create). Both go via the connect-router so admin auth is checked.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        if (isEdit) {
          const res = await editAutomationRule(Number(id));
          if (!res?.ok) {
            setError(res?.message || res?.error || "Kunne ikke hente regelen");
            return;
          }
          if (cancelled) return;
          const r = res.rule;
          setIsSystem(!!r.isSystem);
          // Derive amount/unit/direction from stored signed minutes — keeps
          // the form bidirectional (DB → form → DB round-trips cleanly).
          const sched = formFromOffsetMin(r.scheduleOffsetMin);
          setForm({
            name: r.name || "",
            enabled: !!r.enabled,
            triggerType: r.triggerType || "event",
            eventType: r.eventType || "",
            scheduleAnchor: r.scheduleAnchor || "delivery.date",
            scheduleAmount: sched.scheduleAmount,
            scheduleUnit: sched.scheduleUnit,
            scheduleDirection: sched.scheduleDirection,
            actionType: r.actionType || "send_email",
            actionConfig: r.actionConfig || {},
            delayMinutes: Number(r.delayMinutes || 0),
            notes: r.notes || "",
          });
          setMeta({
            event_types: res.meta?.event_types || [],
            action_types: res.meta?.action_types || [],
            email_types: res.meta?.email_types || [],
            email_type_options: res.meta?.email_type_options || [],
          });
        } else {
          // Just need the meta options for the dropdowns. Use list endpoint.
          const res = await getAutomationRules({ limit: 1, page_number: 1 });
          if (cancelled) return;
          if (res?.ok) {
            setMeta({
              event_types: res.meta?.event_types || [],
              action_types: res.meta?.action_types || [],
              email_types: res.meta?.email_types || [],
              email_type_options: res.meta?.email_type_options || [],
            });
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setActionConfig(patch) {
    setForm((prev) => ({
      ...prev,
      actionConfig: { ...(prev.actionConfig || {}), ...patch },
    }));
  }

  // When admin changes actionType, reset actionConfig to a sensible default
  // so we never carry stale fields from a previous type.
  function changeActionType(next) {
    let cfg = {};
    if (next === "send_email") cfg = { emailType: "" };
    else if (next === "send_vendor_push") {
      cfg = {
        titleTemplate: "Oppdatering på ordre",
        bodyTemplate: "Endring registrert: {{newValue.date}}",
      };
    } else if (next === "push_to_wc") cfg = { kind: "status" };
    setForm((prev) => ({ ...prev, actionType: next, actionConfig: cfg }));
  }

  const validationError = useMemo(() => {
    if (!form.name.trim()) return "Navn må fylles ut";
    if (form.triggerType === "event" && !form.eventType) return "Velg en event-type";
    if (form.triggerType === "schedule") {
      if (!form.scheduleAnchor) return "Velg en tidsplan-anker";
      const amt = Number(form.scheduleAmount);
      if (!Number.isFinite(amt) || amt < 0) return "Ugyldig tidsverdi";
    }
    if (form.actionType === "send_email" && !form.actionConfig?.emailType) {
      return "Velg e-post-type";
    }
    if (form.actionType === "send_vendor_push") {
      if (!form.actionConfig?.titleTemplate || !form.actionConfig?.bodyTemplate) {
        return "Tittel- og melding-mal må fylles ut";
      }
    }
    if (form.actionType === "push_to_wc" && !form.actionConfig?.kind) {
      return "Velg push-type";
    }
    if (Number(form.delayMinutes) < 0) return "Forsinkelse kan ikke være negativ";
    return "";
  }, [form]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (validationError) {
      notifyCenter("error", validationError);
      return;
    }
    setSaving(true);
    try {
      const isSchedule = form.triggerType === "schedule";
      const payload = {
        name: form.name.trim(),
        enabled: form.enabled,
        trigger_type: form.triggerType,
        event_type: isSchedule ? null : form.eventType || null,
        schedule_anchor: isSchedule ? form.scheduleAnchor : null,
        schedule_offset_min: isSchedule
          ? offsetMinFromForm(form.scheduleAmount, form.scheduleUnit, form.scheduleDirection)
          : null,
        action_type: form.actionType,
        action_config: form.actionConfig,
        // delayMinutes is event-only; schedule rules use scheduleOffsetMin instead.
        delay_minutes: isSchedule ? 0 : Number(form.delayMinutes) || 0,
        notes: form.notes ? form.notes.trim() : null,
      };
      if (isEdit) payload.rule_id = Number(id);
      const res = await saveAutomationRule(payload);
      if (res?.ok) {
        notifyCenter("success", res.message || (isEdit ? "Oppdatert" : "Opprettet"));
        navigate("/automation-rules");
      } else {
        notifyCenter("error", res?.message || res?.error || "Lagring feilet");
      }
    } catch (err) {
      notifyCenter("error", err?.message || "Lagring feilet");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <SoftBox py={6} display="flex" justifyContent="center">
          <CircularProgress />
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        <Card component="form" onSubmit={handleSubmit}>
          {/* Header */}
          <SoftBox p={3}>
            <SoftBox display="flex" alignItems="center" gap={1}>
              {isSystem && <LockOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />}
              <SoftTypography variant="h5" fontWeight="bold">
                {isEdit ? "Rediger automatiseringsregel" : "Ny automatiseringsregel"}
              </SoftTypography>
            </SoftBox>
            <SoftTypography variant="button" color="text">
              Bind en hendelse til en handling — e-post, vendor-push eller WC-synk.
            </SoftTypography>
          </SoftBox>

          <Divider sx={{ my: 0 }} />

          {/* Info-callout */}
          <SoftBox p={3}>
            <Alert severity="info" sx={{ mb: 0 }}>
              <SoftTypography variant="caption" component="span" fontWeight="bold">
                Slik fungerer regler:
              </SoftTypography>{" "}
              <SoftTypography variant="caption" component="span">
                Velg en <strong>trigger</strong> (en hendelse i systemet), velg
                en <strong>handling</strong>, og eventuelt en{" "}
                <strong>forsinkelse</strong>. Regelen kjører hver gang triggeren
                oppstår, så lenge den er aktivert.
              </SoftTypography>
            </Alert>
            {isSystem && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <SoftTypography variant="caption">
                  Dette er en <strong>systemregel</strong>. Du kan endre
                  finjusteringer (forsinkelse, e-post-type, notater) men ikke
                  hva som trigger den eller hvilken type handling den utfører.
                </SoftTypography>
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <SoftTypography variant="caption">{error}</SoftTypography>
              </Alert>
            )}
          </SoftBox>

          {/* Form fields */}
          <SoftBox px={3} pb={3}>
            <Grid container spacing={2}>
              {/* Name + enabled */}
              <Grid item xs={12} md={9}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Navn *
                </SoftTypography>
                <SoftInput
                  placeholder="F.eks. Send påminnelse 24t før levering"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Aktivert
                </SoftTypography>
                <SoftBox sx={{ height: 40, display: "flex", alignItems: "center" }}>
                  <Switch
                    checked={!!form.enabled}
                    onChange={(e) => setField("enabled", e.target.checked)}
                  />
                  <SoftTypography variant="caption" color="text" ml={1}>
                    {form.enabled ? "Ja" : "Nei"}
                  </SoftTypography>
                </SoftBox>
              </Grid>

              {/* Trigger section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <SoftTypography variant="button" fontWeight="bold" color="text" textTransform="uppercase">
                  Trigger
                </SoftTypography>
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Type
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.triggerType}
                    onChange={(e) => setField("triggerType", e.target.value)}
                    sx={SELECT_SX}
                    disabled={isSystem}
                  >
                    <MenuItem value="event">Event (når noe skjer)</MenuItem>
                    <MenuItem value="schedule">Tidsplan (relativt til en dato)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {form.triggerType === "event" && (
                <Grid item xs={12} md={8}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                    Event-type *
                  </SoftTypography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.eventType}
                      displayEmpty
                      onChange={(e) => setField("eventType", e.target.value)}
                      sx={SELECT_SX}
                      disabled={isSystem}
                    >
                      <MenuItem value="">Velg event...</MenuItem>
                      {meta.event_types.map((ev) => (
                        <MenuItem key={ev} value={ev}>
                          {EVENT_LABELS[ev] || ev} <code style={{ marginLeft: 8, color: "#999" }}>({ev})</code>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {form.triggerType === "schedule" && (
                <>
                  <Grid item xs={12} md={4}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                      Antall *
                    </SoftTypography>
                    <SoftInput
                      type="number"
                      inputProps={{ min: 0 }}
                      value={form.scheduleAmount}
                      onChange={(e) => setField("scheduleAmount", e.target.value)}
                      disabled={isSystem}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                      Enhet *
                    </SoftTypography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={form.scheduleUnit}
                        onChange={(e) => setField("scheduleUnit", e.target.value)}
                        sx={SELECT_SX}
                        disabled={isSystem}
                      >
                        <MenuItem value="minutes">Minutter</MenuItem>
                        <MenuItem value="hours">Timer</MenuItem>
                        <MenuItem value="days">Dager</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                      Retning *
                    </SoftTypography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={form.scheduleDirection}
                        onChange={(e) => setField("scheduleDirection", e.target.value)}
                        sx={SELECT_SX}
                        disabled={isSystem}
                      >
                        <MenuItem value="before">Før</MenuItem>
                        <MenuItem value="after">Etter</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                      Anker *
                    </SoftTypography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={form.scheduleAnchor}
                        onChange={(e) => setField("scheduleAnchor", e.target.value)}
                        sx={SELECT_SX}
                        disabled={isSystem}
                      >
                        <MenuItem value="delivery.date">Leveringsdato</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 0 }}>
                      <SoftTypography variant="caption">
                        Cron-tikken kjører hvert 5. minutt. En regel fyrer{" "}
                        <strong>én gang per leveranse</strong> når leveringsdato + tid
                        treffer vinduet. Mangler tid på en leveranse, antas{" "}
                        <code>08:00</code>. Misset vindu (server nede) hentes ikke inn igjen.
                      </SoftTypography>
                    </Alert>
                  </Grid>
                </>
              )}

              {/* Action section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <SoftTypography variant="button" fontWeight="bold" color="text" textTransform="uppercase">
                  Handling
                </SoftTypography>
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Type
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.actionType}
                    onChange={(e) => changeActionType(e.target.value)}
                    sx={SELECT_SX}
                    disabled={isSystem}
                  >
                    {meta.action_types.map((a) => (
                      <MenuItem key={a} value={a}>
                        {ACTION_LABELS[a] || a}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Action-specific config */}
              {form.actionType === "send_email" && (
                <Grid item xs={12} md={8}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                    E-postmal *
                  </SoftTypography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.actionConfig?.emailType || ""}
                      displayEmpty
                      onChange={(e) => setActionConfig({ emailType: e.target.value })}
                      sx={SELECT_SX}
                    >
                      <MenuItem value="">Velg mal…</MenuItem>
                      {(() => {
                        // Prefer the rich option list (with template names);
                        // fall back to flat email_types for old backends.
                        const opts = meta.email_type_options?.length
                          ? meta.email_type_options
                          : meta.email_types.map((v) => ({ value: v, label: v, templates: [] }));
                        return opts.map((opt) => {
                          const hasTpl = opt.templates && opt.templates.length > 0;
                          const labelText = hasTpl
                            ? `${opt.templates[0].name}`
                            : `(ingen mal — ${opt.value})`;
                          return (
                            <MenuItem key={opt.value} value={opt.value}>
                              <span>
                                {labelText}{" "}
                                <code style={{ marginLeft: 6, color: "#999" }}>
                                  ({opt.value})
                                </code>
                              </span>
                            </MenuItem>
                          );
                        });
                      })()}
                    </Select>
                  </FormControl>
                  <SoftTypography variant="caption" color="text" mt={0.5} display="block">
                    Velg malen som skal sendes. Mottakerne (kunde/admin/vendor)
                    styres av malens <code>Sendes til</code>-felt på{" "}
                    <a href="/email-settings" style={{ color: "#3b82f6" }}>/email-settings</a>.
                  </SoftTypography>
                </Grid>
              )}

              {form.actionType === "send_vendor_push" && (
                <>
                  <Grid item xs={12} md={8}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                      Tittel-mal *
                    </SoftTypography>
                    <SoftInput
                      placeholder="Oppdatering på ordre"
                      value={form.actionConfig?.titleTemplate || ""}
                      onChange={(e) => setActionConfig({ titleTemplate: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                      Melding-mal *
                    </SoftTypography>
                    <SoftInput
                      multiline
                      minRows={2}
                      placeholder="Endring registrert: {{newValue.date}}"
                      value={form.actionConfig?.bodyTemplate || ""}
                      onChange={(e) => setActionConfig({ bodyTemplate: e.target.value })}
                    />
                    <SoftTypography variant="caption" color="text" mt={0.5} display="block">
                      Bruk <code>{`{{newValue.date}}`}</code>, <code>{`{{newValue.time}}`}</code> eller <code>{`{{orderId}}`}</code> for å sette inn data.
                    </SoftTypography>
                  </Grid>
                </>
              )}

              {form.actionType === "push_to_wc" && (
                <Grid item xs={12} md={8}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                    Hva skal pushes? *
                  </SoftTypography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.actionConfig?.kind || ""}
                      displayEmpty
                      onChange={(e) => setActionConfig({ kind: e.target.value })}
                      sx={SELECT_SX}
                    >
                      <MenuItem value="">Velg...</MenuItem>
                      {PUSH_TO_WC_KINDS.map((k) => (
                        <MenuItem key={k.value} value={k.value}>
                          {k.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Delay — bare relevant for event-trigger; schedule har sin egen offset */}
              {form.triggerType === "event" && (
                <Grid item xs={12} md={4}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                    Forsinkelse (minutter)
                  </SoftTypography>
                  <SoftInput
                    type="number"
                    inputProps={{ min: 0 }}
                    placeholder="0"
                    value={form.delayMinutes}
                    onChange={(e) => setField("delayMinutes", e.target.value)}
                  />
                  <SoftTypography variant="caption" color="text" mt={0.5} display="block">
                    0 = kjør umiddelbart. Større verdi = vent N min etter at triggeren oppstår.
                  </SoftTypography>
                </Grid>
              )}

              {/* Notes */}
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Notater
                </SoftTypography>
                <SoftInput
                  multiline
                  minRows={2}
                  placeholder="Hvorfor finnes denne regelen? Hvem eier den?"
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                />
              </Grid>
            </Grid>
          </SoftBox>

          {/* Footer */}
          <Divider sx={{ my: 0 }} />
          <SoftBox display="flex" justifyContent="flex-end" gap={1} p={3}>
            <SoftButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => navigate("/automation-rules")}
              disabled={saving}
            >
              Avbryt
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              type="submit"
              disabled={saving || !!validationError}
            >
              {saving ? <CircularProgress size={16} /> : isEdit ? "Oppdater" : "Lagre"}
            </SoftButton>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CreateAutomationRule;
